<template>
  <div class="tz-name-heatmap">
    <div class="tz-name-heatmap__buttons">
      <button
        aria-label="Zoom in to map"
        class="tz-name-heatmap__zoom-in"
        @click="zoom(1)"
      />
      <button
        aria-label="Zoom out of map"
        class="tz-name-heatmap__zoom-out"
        @click="zoom(-1)"
      />
    </div>
    <span class="tz-name-heatmap__tooltip" :style="tooltipStyle">
      {{ pointedLocation }}
    </span>
    <svg
      class="tz-name-heatmap__map"
      :id="mapId"
      :class="{ 'tz-name-heatmap__map--panning': isPanning }"
      viewBox="-50 -50 2000 1100"
      :fill="color"
    >
      <component
        v-for="item in map"
        :is="isStateSelected(item.name) ? 'a' : 'g'"
        :key="item.abbr"
        :href="setLink(item.name)"
        :class="{
          'tz-name-heatmap__link': isStateSelected(item.name),
          'tz-name-heatmap__group': !isStateSelected(item.name)
        }"
        @mouseover.capture="pointLocation"
        @mouseout.capture="unPointLocation"
        @mousemove.capture="moveOnLocation"
      >
        <path
          :d="item.d"
          :id="item.name"
          class="tz-name-heatmap__state"
          :class="{
            'tz-name-heatmap__state--active': isStateSelected(item.name)
          }"
        />
        <text
          class="tz-name-heatmap__state-abbr"
          :x="item.x"
          :y="item.y"
          v-text="item.abbr"
        />
      </component>
      <a
        :href="setLink(item.displayName)"
        v-for="item in pinnedStates"
        :key="item.displayName"
      >
        <svg
          :y="`${item.y - 100}`"
          :x="`${item.x - 20}`"
          height="80px"
          width="80px"
          viewBox="0 0 16 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.00065 1.66675C6.23254 1.66675 4.53685 2.36913 3.28661 3.61937C2.03636 4.86961 1.33398 6.5653 1.33398 8.33341C1.33398 9.91008 1.66898 10.9417 2.58398 12.0834L8.00065 18.3334L13.4173 12.0834C14.3323 10.9417 14.6673 9.91008 14.6673 8.33341C14.6673 6.5653 13.9649 4.86961 12.7147 3.61937C11.4645 2.36913 9.76876 1.66675 8.00065 1.66675V1.66675Z"
            :stroke="pinColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            :fill="pinColor"
          />
          <circle cx="8" cy="8" r="6" fill="#FFF" />
        </svg>
        <text
          class="tz-name-heatmap__pin-count"
          :x="`${item.x - 20 + 80 / 2}`"
          :y="`${item.y - 105 + 80 / 2}`"
          v-text="calculateStateText(item.displayName)"
          text-anchor="middle"
          dominant-baseline="middle"
          :textLength="calculateTextLength(item.displayName)"
        />
      </a>
    </svg>
  </div>
</template>

<script>
import PanZoom from '@panzoom/panzoom';
import UsStates from './assets/json/us-states.json';

export default {
  name: 'TzNameHeatmap',
  data() {
    return {
      pointedLocation: null,
      tooltipStyle: null,
      panzoom: null,
      isPanning: false
    };
  },
  created() {
    this.mapSelectedStatesCoordinates();
  },
  mounted() {
    this.heatmapElement = document.getElementById(this.mapId);
    if (this.heatmapElement) {
      this.panzoom = PanZoom(this.heatmapElement, {
        maxScale: 3,
        minScale: 0.5,
        cursor: 'move'
      });

      this.heatmapElement.addEventListener('panzoompan', () => {
        this.isPanning = true;
      });
      this.heatmapElement.addEventListener('panzoomend', () => {
        this.isPanning = false;
      });
      this.heatmapElement.addEventListener('panzoomzoom', () => {
        this.isPanning = false;
      });
    }
  },
  beforeUpdate() {
    this.mapSelectedStatesCoordinates();
  },
  beforeDestroy() {
    this.heatmapElement.addEventListener('panzoompan', () => {
      this.isPanning = true;
    });
    this.heatmapElement.addEventListener('panzoomend', () => {
      this.isPanning = false;
    });
    this.heatmapElement.addEventListener('panzoomzoom', () => {
      this.isPanning = false;
    });
  },
  methods: {
    /**
     * Sets the zoom level when using the zoom in/out buttons
     *
     * @param {number} level
     */
    zoom(level) {
      level === -1 ? this.panzoom.zoomOut() : this.panzoom.zoomIn();
    },
    /**
     * Returns true or false if state is selected
     *
     * @param {string} stateName
     * @returns {boolean}
     */
    isStateSelected(stateName) {
      const isSelected = this.selectedStates.some(
        state => state.displayName === stateName
      );

      return isSelected;
    },
    /**
     * Sets the link if name exists for that state
     *
     * @param {string} stateName
     * @returns {string|*}
     */
    setLink(stateName) {
      const stateSelected = this.selectedStates.find(
        state => state.displayName === stateName
      );

      if (!stateSelected) {
        return;
      }

      return stateSelected.urlValue;
    },
    /**
     * Grabs the name of the state
     *
     * @param event
     * @returns {*}
     */
    getLocationName(event) {
      if (event && event.id) {
        return event.id;
      } else if (
        event.previousElementSibling &&
        event.previousElementSibling.id
      ) {
        return event.previousElementSibling.id;
      }
      return null;
    },
    /**
     * Displays tooltip when hovering over states
     *
     * @param event
     */
    pointLocation(event) {
      this.pointedLocation = this.getLocationName(event.target);
    },
    /**
     * Hides tooltip when state is no longer hovered
     */
    unPointLocation() {
      this.pointedLocation = null;
      this.tooltipStyle = { display: 'none' };
    },
    /**
     * Updates tooltip position when hovering over states
     *
     * @param event
     */
    moveOnLocation(event) {
      this.tooltipStyle = {
        display: 'block',
        top: `${event.pageY + 5}px`,
        left: `${event.pageX - 5}px`
      };
    },
    /**
     * Text to display on state
     * based on whether or not there are state display results
     */
    calculateStateText(name) {
      // If this.useStateCount use the number in the resultStateCounts object for that state
      const resultsText = this.selectedStates.find(state => {
        return state.displayName === name;
      }).count;

      return Math.abs(resultsText) > 999
        ? Math.sign(resultsText) * (Math.abs(resultsText) / 1000).toFixed(1) +
            'k'
        : Math.sign(resultsText) * Math.abs(resultsText);
    },
    /**
     * Positioning and width of text element to center in pin
     */
    calculateTextLength(state) {
      const text = new String(this.calculateStateText(state));
      let length = text.length * 14;

      for (let i = 0; i < text.length; i++) {
        if (text[i] === '.') {
          length -= 10;
        }
        if (text[i] === '1' || text[i] === 'k') {
          length -= 2;
        }
      }

      return text.length === 1 ? length - 10 : length;
    },
    /**
     * Adds coordinates from the map JSON
     * Sets position of pin to the selectedStates object
     */
    mapSelectedStatesCoordinates() {
      for (let i = 0; i < this.selectedStates.length; i++) {
        const stateFromMap = this.map.find(
          state => state.name === this.selectedStates[i].displayName
        );

        this.selectedStates[i].x = stateFromMap.x;
        this.selectedStates[i].y = stateFromMap.y;
      }
    }
  },
  computed: {
    /**
     * Pre-filtered list to display pins on
     * Only pin place pins on states in the selectedStates array that have a valid count
     */
    pinnedStates() {
      if (!this.useStateCount || !this.selectedStates) return new Array();

      return this.selectedStates.filter(item => !!item.count);
    }
  },
  props: {
    /**
     * Array that stores the path, state name and abbreviation coming from the JSON file.
     * Used to create the SVG map
     */
    map: {
      type: Array,
      default: () => UsStates
    },
    /**
     * Dynamically set svg ID
     * @values string
     */
    mapId: {
      type: String,
      required: true
    },
    /**
     * Changes the color of the map
     * @values string, rgb, hex
     */
    color: {
      type: String,
      default: '#c0c1ca'
    },
    /**
     * Array containing state name and url.
     * Used to highlight the state(s) in the map.
     * Array should already exist on the SEO pages on the framework side
     */
    selectedStates: {
      type: Array,
      default: () => new Array()
    },
    /**
     * True will display the state's results number if there is a result number
     * */
    useStateCount: {
      type: Boolean,
      default: false
    },
    /**
     * A hexidecimal string that sets the color of the pin
     */
    pinColor: {
      type: String,
      default: '#FF9822'
    }
  }
};
</script>

<style lang="scss">
.tz-name-heatmap {
  border: solid 2px #555;
  overflow: hidden;

  &__map {
    stroke: #fff;
    stroke-width: 1;
    stroke-linecap: round;
    stroke-linejoin: round;
    user-select: none;
    touch-action: none;
    &--panning a {
      pointer-events: none;
    }
  }

  &__state-abbr,
  &__pin-count {
    fill: #000;
    font-size: 24px;
    stroke-width: 0;
  }

  &__tooltip {
    color: #fff;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.7);
    padding: 10px;
    border-radius: 8px;
    display: none;
    position: absolute;
    transform: translateX(-100%);
  }

  &__state--active {
    fill: #6b6ec7;
  }
  .tz-name-heatmap__link:hover .tz-name-heatmap__state--active {
    fill: #a3a4dc;
  }

  &__buttons {
    position: relative;
    z-index: 1;
  }

  &__zoom-in,
  &__zoom-out {
    height: 40px;
    width: 40px;
    margin: 5px;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: contain;
  }
  &__zoom-in {
    background-image: url('./assets/images/plus.svg');
  }

  &__zoom-out {
    background-image: url('./assets/images/minus.svg');
  }
}
</style>
